import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PARTNER_CATEGORIES,
  PARTNER_CATEGORY_LABELS,
  PARTNER_TIERS,
  PARTNER_TIER_LABELS,
  PROSPECT_SOURCES,
  PROSPECT_SOURCE_LABELS,
  PartnerCategory,
  PartnerTier,
  ProspectSource,
} from '../../constants/partnerNetwork';
import {
  DuplicateMatch,
  DuplicateProspectError,
  Prospect,
  ProspectInput,
  checkDuplicates,
  createProspect,
  updateProspect,
} from '../../services/partnerNetwork';
import { toDateInput } from '../../utils/partnerFormat';
import { Alert, Modal, inputClass } from './ui';

interface Props {
  open: boolean;
  /** Absent for a new prospect. */
  prospect?: Prospect | null;
  onClose: () => void;
  onSaved: (prospect: Prospect, created: boolean) => void;
}

const emptyForm = {
  fullName: '',
  companyName: '',
  jobTitle: '',
  email: '',
  phone: '',
  country: '',
  city: '',
  website: '',
  linkedinUrl: '',
  category: '' as PartnerCategory | '',
  tier: '' as PartnerTier | '',
  industry: '',
  source: '' as ProspectSource | '',
  nextFollowUpAt: '',
};

type FormState = typeof emptyForm;

const DUPLICATE_FIELD_LABELS: Record<DuplicateMatch['field'], string> = {
  email: 'the same email address',
  phone: 'the same phone number',
  companyName: 'the same company name',
  linkedinUrl: 'the same LinkedIn profile',
};

/**
 * Add / edit a prospect.
 *
 * Duplicates are checked as the marketer types the identifying fields and
 * warned about inline, rather than only on submit — the point is to stop the
 * duplicate being typed out in full, not to reject it afterwards.
 */
const ProspectForm: React.FC<Props> = ({ open, prospect, onClose, onSaved }) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [acknowledgedDuplicates, setAcknowledgedDuplicates] = useState(false);

  const isEdit = Boolean(prospect);

  useEffect(() => {
    if (!open) return;

    setError(null);
    setDuplicates([]);
    setAcknowledgedDuplicates(false);

    setForm(
      prospect
        ? {
            fullName: prospect.fullName,
            companyName: prospect.companyName,
            jobTitle: prospect.jobTitle,
            email: prospect.email,
            phone: prospect.phone,
            country: prospect.country,
            city: prospect.city,
            website: prospect.website,
            linkedinUrl: prospect.linkedinUrl,
            category: prospect.category || '',
            tier: prospect.tier || '',
            industry: prospect.industry,
            source: prospect.source || '',
            nextFollowUpAt: toDateInput(prospect.nextFollowUpAt),
          }
        : emptyForm
    );
  }, [open, prospect]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  /* --- duplicate lookup, debounced on the identifying fields --- */

  const identity = useMemo(
    () => ({
      email: form.email.trim(),
      phone: form.phone.trim(),
      companyName: form.companyName.trim(),
      linkedinUrl: form.linkedinUrl.trim(),
    }),
    [form.email, form.phone, form.companyName, form.linkedinUrl]
  );

  // Tracks the latest lookup so a slow earlier response cannot overwrite the
  // result of a later one.
  const lookupId = useRef(0);

  const runDuplicateCheck = useCallback(async () => {
    const hasSomethingToMatch =
      identity.email || identity.phone || identity.companyName || identity.linkedinUrl;

    if (!hasSomethingToMatch) {
      setDuplicates([]);
      return;
    }

    const id = ++lookupId.current;

    try {
      const { duplicates: found } = await checkDuplicates({
        ...identity,
        excludeId: prospect?.id,
      });
      if (id === lookupId.current) setDuplicates(found);
    } catch {
      // A failed duplicate check must never block adding a prospect.
      if (id === lookupId.current) setDuplicates([]);
    }
  }, [identity, prospect?.id]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(runDuplicateCheck, 450);
    return () => window.clearTimeout(timer);
  }, [open, runDuplicateCheck]);

  /* --- submit --- */

  const buildPayload = (): ProspectInput => ({
    fullName: form.fullName.trim(),
    companyName: form.companyName.trim(),
    jobTitle: form.jobTitle.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    country: form.country.trim(),
    city: form.city.trim(),
    website: form.website.trim(),
    linkedinUrl: form.linkedinUrl.trim(),
    category: form.category || null,
    tier: form.tier || null,
    industry: form.industry.trim(),
    source: form.source || null,
    nextFollowUpAt: form.nextFollowUpAt || null,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.fullName.trim()) {
      setError('A full name is required.');
      return;
    }

    try {
      setSaving(true);

      if (prospect) {
        const updated = await updateProspect(prospect.id, buildPayload());
        onSaved(updated, false);
      } else {
        const created = await createProspect({
          ...buildPayload(),
          // The warning has already been shown and dismissed by this point.
          force: acknowledgedDuplicates,
        });
        onSaved(created, true);
      }

      onClose();
    } catch (err) {
      if (err instanceof DuplicateProspectError) {
        setDuplicates(err.duplicates);
        setError(
          `${err.message} Review the match below, then choose "Add anyway" if this is a different person.`
        );
      } else {
        setError(err instanceof Error ? err.message : 'Could not save the prospect');
      }
    } finally {
      setSaving(false);
    }
  };

  const blockedByDuplicates = !isEdit && duplicates.length > 0 && !acknowledgedDuplicates;

  return (
    <Modal
      open={open}
      size="lg"
      title={isEdit ? 'Edit prospect' : 'Add prospect'}
      description={
        isEdit
          ? `${prospect?.prospectId} — changes are recorded on the timeline.`
          : 'Only the name is required. Everything else can be filled in as you learn it.'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary py-2.5 px-5">
            Cancel
          </button>
          <button
            type="submit"
            form="prospect-form"
            disabled={saving || blockedByDuplicates}
            className="btn-primary py-2.5 px-5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add prospect'}
          </button>
        </>
      }
    >
      <form id="prospect-form" onSubmit={handleSubmit} className="space-y-6">
        {error && <Alert tone="error">{error}</Alert>}

        {duplicates.length > 0 && (
          <Alert tone="warning">
            <p className="font-medium">A similar prospect already exists.</p>
            <ul className="mt-2 space-y-1.5">
              {duplicates.map((match) => (
                <li key={match.prospect.id}>
                  <Link
                    to={`/admin/partners/prospects/${match.prospect.id}`}
                    className="underline underline-offset-2"
                  >
                    {match.prospect.fullName}
                    {match.prospect.companyName ? ` — ${match.prospect.companyName}` : ''}
                  </Link>{' '}
                  <span className="opacity-80">
                    has {DUPLICATE_FIELD_LABELS[match.field]}.
                  </span>
                </li>
              ))}
            </ul>
            {!isEdit && (
              <label className="mt-3 flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={acknowledgedDuplicates}
                  onChange={(e) => setAcknowledgedDuplicates(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-amber-400"
                />
                <span>
                  This is a different person — add anyway.
                </span>
              </label>
            )}
          </Alert>
        )}

        <section>
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted">
            Basic information
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-2 block text-sm text-muted">Full name *</label>
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => set('fullName', e.target.value)}
                placeholder="John Adeyemi"
                autoFocus
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Company</label>
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => set('companyName', e.target.value)}
                placeholder="ABC Hospitality Consulting"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Job title</label>
              <input
                type="text"
                value={form.jobTitle}
                onChange={(e) => set('jobTitle', e.target.value)}
                placeholder="Managing Partner"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="john@abcconsulting.com"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="+234 803 000 0000"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Country</label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => set('country', e.target.value)}
                placeholder="Nigeria"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder="Abuja"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Website</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => set('website', e.target.value)}
                placeholder="https://abcconsulting.com"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">LinkedIn</label>
              <input
                type="url"
                value={form.linkedinUrl}
                onChange={(e) => set('linkedinUrl', e.target.value)}
                placeholder="https://linkedin.com/in/…"
                className={inputClass}
              />
            </div>
          </div>
        </section>

        <section>
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted">
            Partner information
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm text-muted">Category</label>
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value as PartnerCategory | '')}
                className={inputClass}
              >
                <option value="">Not set</option>
                {PARTNER_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {PARTNER_CATEGORY_LABELS[category]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Tier</label>
              <select
                value={form.tier}
                onChange={(e) => set('tier', e.target.value as PartnerTier | '')}
                className={inputClass}
              >
                <option value="">Not set</option>
                {PARTNER_TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {PARTNER_TIER_LABELS[tier]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Industry</label>
              <input
                type="text"
                value={form.industry}
                onChange={(e) => set('industry', e.target.value)}
                placeholder="Hotels"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Source</label>
              <select
                value={form.source}
                onChange={(e) => set('source', e.target.value as ProspectSource | '')}
                className={inputClass}
              >
                <option value="">Not set</option>
                {PROSPECT_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {PROSPECT_SOURCE_LABELS[source]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section>
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted">
            Follow-up
          </h3>
          <div className="sm:max-w-xs">
            <label className="mb-2 block text-sm text-muted">Next follow-up</label>
            <input
              type="date"
              value={form.nextFollowUpAt}
              onChange={(e) => set('nextFollowUpAt', e.target.value)}
              className={inputClass}
            />
            <p className="mt-2 text-xs text-muted">
              Leave empty and they show under "No follow-up set".
            </p>
          </div>
        </section>
      </form>
    </Modal>
  );
};

export default ProspectForm;
